// frontend/src/store/project-form-provider.tsx
// Provider is the single place that performs network I/O and orchestration (calls each slice to validate/build payload, then does the API calls in sequence, refetches results, commits, toasts).
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

// Import other store modules / slices
import { useProjectGeneralSlice } from "@/store/project-general-slice";
import { useProjectOpexSlice, ProjectOpexRowUI } from "@/store/project-opex-slice";

// Import utils
import api from "@/utils/axios";
import { useProjectData } from "@/utils/use-project-data";
import { showToast } from "@/utils/toast";



type Primitive = string | number | boolean | null | undefined;
export type ProjectData = Record<string, Primitive>;

export interface YearlyValue {
  Year: number;
  [key: string]: number;
}

export interface ProjectModelResults {
  revenue: YearlyValue[];
  opex: YearlyValue[];
  interest: YearlyValue[];
  redemption: YearlyValue[];
  debt_service: YearlyValue[];
  remaining_debt: YearlyValue[];
  debt: YearlyValue[];
  profit: YearlyValue[];
  investment: YearlyValue[];
  taxes: YearlyValue[];
  cashflow: YearlyValue[];
  // add key metrics if your API returns them
  [key: string]: any;
}

interface Ctx {
  project: ProjectData | null;
  projectFieldMetadata: any[];
  loading: boolean;
  error: string | null;
  formData: ProjectData;
  updateField: (name: string, value: Primitive) => void;
  resetForm: () => void;
  resetAll: () => void;
  commitForm: () => void;
  savingAll: boolean;
  saveAll: () => Promise<void>;
  refetch: (opts?: { background?: boolean }) => Promise<void> | void;
  // Dirty tracking
  isAnyDirty: boolean; // Combined dirty flag (read-only convenience)
  isDirty: boolean;
  dirtyKeys: string[];
  // OpEx slice (renamed types)
  opexRows: ProjectOpexRowUI[] | null;
  setOpexRows: React.Dispatch<React.SetStateAction<ProjectOpexRowUI[] | null>>;
  loadOpexItems: () => Promise<void>;
  saveOpexItems: () => Promise<void>;
  resetOpexItems: () => void;
  opexSaving: boolean;
  opexError: string | null;
  isOpexDirty: boolean;
  // Results cache
  results: ProjectModelResults | null;
  resultsLoading: boolean;
  resultsError: string | null;
  fetchResults: () => Promise<void>;
  // Field type and options
  getFieldType: (fieldName: string) => string | undefined;
  getFieldOptions: (fieldName: string) => string[] | undefined;
}

const ProjectFormContext = createContext<Ctx | null>(null);

export function ProjectFormProvider({ children }: { children: React.ReactNode }) {
  const { project, projectFieldMetadata, loading, error, refetch } = useProjectData();

  const pid = (project as any)?.project_id as number | undefined;

  const { t } = useTranslation();

  // Field metadata helpers
  const getFieldType = useCallback((fieldName: string): string | undefined => {
    const meta = projectFieldMetadata?.find((f: any) => f.field_name === fieldName);
    return meta?.type;
  }, [projectFieldMetadata]);

  const getFieldOptions = useCallback((fieldName: string): string[] | undefined => {
    const meta = projectFieldMetadata?.find((f: any) => f.field_name === fieldName);
    const opts = (meta as any)?.allowed_values;
    const list = Array.isArray(opts)
      ? opts
      : (typeof opts === "string" ? (opts as string).split(",").map((s) => s.trim()).filter(Boolean) : []);
    return list.length ? list : undefined;
  }, [projectFieldMetadata]);

  // General slice
  const {
    formData, updateField, resetForm, commitForm, dirtyKeys, isDirty, validate, getChangedFields,
  } = useProjectGeneralSlice(project, projectFieldMetadata);

  // OpEx slice
  const {
    opexRows, setOpexRows, loadOpexItems, saveOpexItems, resetOpexItems, commitOpex, opexSaving, opexError, isOpexDirty, validate: validateOpex
  } = useProjectOpexSlice(project);

  // Results cache
  const [results, setResults] = useState<ProjectModelResults | null>(null);
  const [resultsLoading, setResultsLoading] = useState<boolean>(false);
  const [resultsError, setResultsError] = useState<string | null>(null);

  const [savingAll, setSavingAll] = useState(false);

  // Combined dirty flag
  const isAnyDirty = isDirty || isOpexDirty;

  const fetchResults = useCallback(async () => {
    if (!pid) return;
    setResultsLoading(true);
    try {
      const res = await api.post(`/api/projects/${pid}/calculate`);
      setResults(res.data);
      setResultsError(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        t("project.form.failed_to_load_results");
      setResultsError(msg);
    } finally {
      setResultsLoading(false);
    }
  }, [pid, t]);

  const resetAll = useCallback(() => {
    resetForm();        // general slice
    resetOpexItems();   // OpEx slice
  }, [resetForm, resetOpexItems]);

  const commitAll = useCallback(() => {
    commitForm();       // general slice
    commitOpex();       // OpEx slice
  }, [commitForm, commitOpex]);  


  const saveGeneral = useCallback(async () => {
    if (!pid || dirtyKeys.length === 0) return;

    // Build minimal payload with only changed scalar fields
    const payload = getChangedFields?.() ?? {};
    if (Object.keys(payload).length === 0) return;

    await api.put(`/api/projects/${pid}`, payload);
    // Do not commit here; commit after all module saves succeed in saveAll()
    
  }, [pid, getChangedFields, commitForm]);



  const saveAll = useCallback(async () => {
    if (!isAnyDirty) {
      showToast(t("project.form.no_changes"), "warning");
      return;
    }

    // Client-side validation before any writes (metadata-driven)
    const genIssues = validate?.() ?? [];
    const opexIssues = validateOpex?.() ?? [];
    if (genIssues.length > 0 || opexIssues.length > 0) {
      const messages = [
        ...genIssues.map(i => i?.path ? `${i.path}: ${i.message}` : i.message),
        ...opexIssues.map(i => {
          const row = (i.rowIndex ?? 0) + 1;
          return `${t("project.opex.validation_row_prefix", { row })}: ${i.message}`;
        }),
      ];
      showToast(messages.join("\n"), "error");
      return;
    }

    setSavingAll(true);

    try {

      // Save General (if dirty)
      await saveGeneral();

      // Save OpEx items (if dirty)
      if (isOpexDirty) {
        await saveOpexItems();
      }

      // Success feedback; then clear dirty flags so the chip disappears right after
      showToast(t("project.form.all_changes_saved"), "success");
      commitAll();

      // Background refreshes (silent -> no provider loading flicker)
      Promise.resolve(refetch?.({ background: true })).catch(() => {});
      Promise.resolve(loadOpexItems()).catch(() => {});

      if (pid) {
        // Notify listeners that a save completed (e.g., results recalculation)
        document.dispatchEvent(new CustomEvent("project:saved", { detail: { projectId: pid } }));
      }

    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        t("project.form.failed_to_save_changes");
      showToast(msg, "error");
      throw err; // surface if a page wants to handle
    } finally {
      setSavingAll(false);
    }
  }, [pid, isAnyDirty, saveGeneral, isOpexDirty, saveOpexItems,  refetch, loadOpexItems, commitAll]);



  // Initial prefetch of results once project is available
  useEffect(() => {
    if (pid) fetchResults();
  }, [pid, fetchResults]);


  // Also refresh results when a save completes (listen to the event)
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ projectId?: string | number }>;
      if (!ev?.detail?.projectId || !pid) return;
      if (String(ev.detail.projectId) === String(pid)) {
        fetchResults();
      }
    };
    document.addEventListener("project:saved", handler as EventListener);
    return () => document.removeEventListener("project:saved", handler as EventListener);
  }, [pid, fetchResults]);

  
  const value = useMemo<Ctx>(
    () => ({
      project,
      projectFieldMetadata,
      loading: Boolean(loading),
      error: error ?? null,
      formData,
      updateField,
      resetForm,
      commitForm,
      refetch,
      resetAll,
      commitAll,
      savingAll,
      saveAll,
      isAnyDirty,
      isDirty,
      dirtyKeys,

      // OpEx slice
      opexRows,
      setOpexRows,
      loadOpexItems,
      saveOpexItems,
      resetOpexItems,
      commitOpex,
      opexSaving,
      opexError,
      isOpexDirty,
      
      // Results
      results,
      resultsLoading,
      resultsError,
      fetchResults,

      // Field type and options
      getFieldType,
      getFieldOptions,
    }),
    [
      project,
      projectFieldMetadata,
      loading,
      error,
      formData,
      updateField,
      resetForm,
      commitForm,
      refetch,
      resetAll,
      commitAll,
      saveAll,
      savingAll,
      isAnyDirty,
      isDirty,
      dirtyKeys,

      // OpEx slice
      opexRows,
      setOpexRows,
      loadOpexItems,
      saveOpexItems,
      resetOpexItems,
      commitOpex,
      opexSaving,
      opexError,
      isOpexDirty,

      // Results
      results,
      resultsLoading,
      resultsError,
      fetchResults,
      
      // Field type and options
      getFieldType,
      getFieldOptions,
    ]
  );

  return <ProjectFormContext.Provider value={value}>{children}</ProjectFormContext.Provider>;
}

export function useProjectFormStore() {
  const ctx = useContext(ProjectFormContext);
  if (!ctx) throw new Error("useProjectFormStore must be used within ProjectFormProvider");
  return ctx;
}

export function useProjectFormStoreOptional() {
  return useContext(ProjectFormContext);
}